import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import PrefecturaReportsSection from '@/components/PrefecturaReportsSection';
import { toast } from 'sonner';
import { Search, AlertTriangle, User, Plus, RotateCcw, RefreshCw, MessageSquare, CheckCircle2, Clock, CalendarClock } from 'lucide-react';
import firstName from '@/utils/firstName';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PrefecturaDashboard = ({ user, onLogout }) => {
  const [term, setTerm] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ motivo: '', descripcion: '', gravedad: 'leve' });
  const [saving, setSaving] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [reportsKey, setReportsKey] = useState(0);
  const [notices, setNotices] = useState([]);
  const [showNoticeHistory, setShowNoticeHistory] = useState(false);
  const [attendingId, setAttendingId] = useState(null);

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const loadNotices = useCallback(() => {
    axios.get(`${API}/schedule-notices`, authHeaders())
      .then(r => setNotices(r.data.notices || []))
      .catch(() => {});
  }, []);

  useEffect(() => { loadNotices(); }, [loadNotices]);

  const attendNotice = async (id) => {
    setAttendingId(id);
    try {
      await axios.patch(`${API}/schedule-notices/${id}/attend`, {}, authHeaders());
      toast.success('Aviso marcado como atendido');
      loadNotices();
    } catch (e) {
      toast.error('No se pudo actualizar el aviso');
    } finally {
      setAttendingId(null);
    }
  };

  const COLOR_DOTS = { green: '#16a34a', amber: '#facc15', red: '#dc2626', na: '#64748b' };
  const COLOR_LABELS = { green: 'Entró a clase', amber: 'Entró tarde / sacó a los alumnos antes', red: 'No se presentó', na: 'Justificado (reunión / comisión)' };
  const activeNotices = notices.filter(n => n.status === 'active');
  const attendedNotices = notices.filter(n => n.status === 'attended');

  const handleReset = async () => {
    setResetting(true);
    try {
      await axios.post(`${API}/prefectura/reset-cycle`, {}, authHeaders());
      toast.success('Reportes reiniciados correctamente');
      setResetOpen(false);
      setReportsKey((k) => k + 1);
    } catch (e) {
      toast.error('Error al reiniciar los reportes');
    } finally {
      setResetting(false);
    }
  };

  const handleSearch = async (override) => {
    const q = (typeof override === 'string' ? override : term).trim();
    if (!q) { toast.error('Escribe el nombre o matrícula del alumno'); return; }
    setSearching(true);
    try {
      const res = await axios.get(`${API}/prefectura/search-students`, { ...authHeaders(), params: { name: q } });
      setResults(res.data.students || []);
      if (!res.data.found) toast.info('No se encontraron alumnos');
    } catch (e) {
      toast.error('Error al buscar');
    } finally {
      setSearching(false);
    }
  };

  const openReport = (student) => {
    setSelected(student);
    setForm({ motivo: '', descripcion: '', gravedad: 'leve' });
    setDialogOpen(true);
  };

  const submitReport = async (e) => {
    e.preventDefault();
    if (!form.motivo.trim()) { toast.error('El motivo es obligatorio'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/prefectura/reports`, {
        student_id: selected.id,
        student_name: selected.name,
        matricula: selected.matricula,
        grupo: selected.grupo,
        turno: selected.turno,
        motivo: form.motivo.trim(),
        descripcion: form.descripcion.trim(),
        gravedad: form.gravedad,
      }, authHeaders());
      toast.success('Reporte levantado correctamente');
      setDialogOpen(false);
      setReportsKey((k) => k + 1);
    } catch (e) {
      toast.error('Error al crear el reporte');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="prefectura-dashboard">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-red-600 flex items-center justify-center">
            <AlertTriangle className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Prefectura</h1>
            <p className="text-sm text-slate-500">
              {firstName(user?.name) ? <>Bienvenido(a), <span className="font-semibold text-slate-700">{firstName(user?.name)}</span> · </> : null}
              Busca un alumno y levanta un reporte de incidencias
            </p>
          </div>
          <button
            onClick={() => { window.location.href = '/horarios'; }}
            data-testid="prefectura-horarios-btn"
            className="ml-auto relative inline-flex items-center gap-1 text-sm px-3 h-9 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-medium"
          >
            <AlertTriangle className="w-4 h-4" /> Horarios
            {activeNotices.length > 0 && (
              <span data-testid="prefectura-horarios-badge" className="absolute -top-2 -right-2 min-w-[20px] h-5 px-1 rounded-full bg-amber-500 text-white text-[11px] font-bold flex items-center justify-center">{activeNotices.length}</span>
            )}
          </button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => { setReportsKey((k) => k + 1); if (results) handleSearch(); }}
            data-testid="prefectura-refresh-btn"
            className="border-slate-300 text-slate-700 hover:bg-slate-100"
          >
            <RefreshCw className="w-4 h-4 mr-1" /> Actualizar
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setResetOpen(true)}
            data-testid="prefectura-reset-btn"
            className="text-red-600 border-red-200 hover:bg-red-50"
          >
            <RotateCcw className="w-4 h-4 mr-1" /> Reiniciar reportes
          </Button>
        </div>

        <Card className="p-4">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Buscar por nombre o matrícula..."
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch(e.target.value)}
                className="pl-10"
                data-testid="prefectura-search-input"
              />
            </div>
            <Button onClick={() => handleSearch()} disabled={searching} data-testid="prefectura-search-btn" className="bg-red-600 hover:bg-red-700">
              {searching ? 'Buscando...' : 'Buscar'}
            </Button>
          </div>

          {results && results.length > 0 && (
            <div className="mt-4 space-y-2" data-testid="prefectura-results">
              {results.map((s) => (
                <div key={s.id} className="flex items-center justify-between p-3 rounded-lg border border-slate-200">
                  <div className="flex items-center gap-3">
                    <User className="w-4 h-4 text-slate-400" />
                    <div>
                      <p className="font-semibold text-slate-800">{s.name}</p>
                      <p className="text-xs text-slate-500">
                        {s.matricula ? `Matrícula: ${s.matricula}` : 'Sin matrícula'}
                        {s.grupo ? ` · ${s.grupo}` : ''}{s.turno ? ` · ${s.turno}` : ''}
                      </p>
                    </div>
                  </div>
                  <Button size="sm" onClick={() => openReport(s)} data-testid={`prefectura-report-${s.id}`} className="bg-red-600 hover:bg-red-700">
                    <Plus className="w-4 h-4 mr-1" /> Reporte
                  </Button>
                </div>
              ))}
            </div>
          )}
          {results && results.length === 0 && <p className="mt-4 text-sm text-slate-500">Sin resultados.</p>}
        </Card>

        {/* Avisos de horarios (de Subdirección / Jefe de Docentes) */}
        <Card className="p-4" data-testid="prefectura-notices-card">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-9 h-9 rounded-lg bg-amber-500 flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-slate-800">Avisos de horarios</h2>
              <p className="text-xs text-slate-500">Correcciones solicitadas por Subdirección o Jefe de Docentes</p>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={loadNotices} data-testid="prefectura-notices-refresh" className="border-slate-300 text-slate-700 hover:bg-slate-100">
                <RefreshCw className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowNoticeHistory(s => !s)} data-testid="prefectura-notices-history-toggle" className="text-slate-600">
                <Clock className="w-4 h-4 mr-1" /> Historial ({attendedNotices.length})
              </Button>
            </div>
          </div>

          {activeNotices.length === 0 ? (
            <p className="text-sm text-slate-500 py-2" data-testid="prefectura-notices-empty">No tienes avisos activos. 🎉</p>
          ) : (
            <div className="space-y-2" data-testid="prefectura-notices-active">
              {activeNotices.map(n => (
                <div key={n.id} className="border border-amber-200 bg-amber-50/60 rounded-lg p-3" data-testid={`prefectura-notice-${n.id}`}>
                  <div className="flex items-start gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap text-sm">
                        <span className="font-semibold text-slate-800">{n.group_name}</span>
                        {n.row != null && <span className="text-xs text-slate-500">· Hora {n.row + 1}, Día {n.col + 1}</span>}
                        {n.current_color && (
                          <span className="inline-flex items-center gap-1 text-xs text-slate-600">
                            · <span className="w-3 h-3 rounded-full inline-block" style={{ background: COLOR_DOTS[n.current_color] || '#e2e8f0' }} />
                            {COLOR_LABELS[n.current_color] || n.current_color}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-700 mt-1">{n.message}</p>
                      <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                        <CalendarClock className="w-3 h-3" /> De {n.from_name} · Semana {n.week_start}
                      </p>
                    </div>
                    <Button size="sm" onClick={() => attendNotice(n.id)} disabled={attendingId === n.id}
                      data-testid={`prefectura-notice-attend-${n.id}`} className="bg-green-600 hover:bg-green-700 shrink-0">
                      <CheckCircle2 className="w-4 h-4 mr-1" /> {attendingId === n.id ? '...' : 'Atendido'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {showNoticeHistory && (
            <div className="mt-4 border-t border-slate-100 pt-3" data-testid="prefectura-notices-attended">
              <p className="text-xs font-semibold text-slate-500 mb-2">Historial (atendidos)</p>
              {attendedNotices.length === 0 ? (
                <p className="text-xs text-slate-400">Sin avisos atendidos aún.</p>
              ) : (
                <div className="space-y-2 max-h-72 overflow-auto">
                  {attendedNotices.map(n => (
                    <div key={n.id} className="border border-slate-100 rounded-lg p-2 text-xs" data-testid={`prefectura-notice-history-${n.id}`}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-700">Atendido</span>
                        <span className="font-medium text-slate-700">{n.group_name}</span>
                        {n.row != null && <span className="text-slate-400">· Hora {n.row + 1}, Día {n.col + 1}</span>}
                      </div>
                      <p className="text-slate-600 mt-1">{n.message}</p>
                      <p className="text-slate-400 mt-0.5">De {n.from_name} · Semana {n.week_start}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </Card>

        {/* Mis reportes (confirmación de atención por el orientador) */}
        <PrefecturaReportsSection key={reportsKey} showStatus canDelete />
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Reporte de Incidencia</DialogTitle>
          </DialogHeader>
          {selected && (
            <form onSubmit={submitReport} className="space-y-4">
              <div className="text-sm text-slate-600 bg-slate-50 rounded-lg p-3">
                <p className="font-semibold text-slate-800">{selected.name}</p>
                <p className="text-xs">{selected.matricula ? `Matrícula: ${selected.matricula}` : 'Sin matrícula'}{selected.grupo ? ` · ${selected.grupo}` : ''}{selected.turno ? ` · ${selected.turno}` : ''}</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="pref-motivo">Motivo</Label>
                <Input id="pref-motivo" data-testid="prefectura-motivo-input" value={form.motivo} onChange={(e) => setForm({ ...form, motivo: e.target.value })} required placeholder="Ej: Falta al reglamento" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pref-desc">Descripción</Label>
                <Textarea id="pref-desc" data-testid="prefectura-desc-input" value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} placeholder="Breve descripción del incidente" />
              </div>
              <div className="space-y-2">
                <Label>Gravedad del incidente</Label>
                <Select value={form.gravedad} onValueChange={(v) => setForm({ ...form, gravedad: v })}>
                  <SelectTrigger data-testid="prefectura-gravedad-select">
                    <SelectValue placeholder="Selecciona la gravedad" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="leve" data-testid="gravedad-option-leve">
                      <span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-green-500" /> Leve</span>
                    </SelectItem>
                    <SelectItem value="moderado" data-testid="gravedad-option-moderado">
                      <span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-amber-500" /> Moderado</span>
                    </SelectItem>
                    <SelectItem value="grave" data-testid="gravedad-option-grave">
                      <span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-red-500" /> Grave</span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" disabled={saving} data-testid="prefectura-submit-report" className="w-full bg-red-600 hover:bg-red-700">
                {saving ? 'Guardando...' : 'Levantar Reporte'}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" /> Reiniciar reportes
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Se eliminarán todos los reportes de prefectura de tu institución. Esta acción no se puede deshacer.
            El <strong>Historial de Conducta</strong> se conserva y no se verá afectado.
          </p>
          <div className="flex gap-2 justify-end mt-2">
            <Button variant="outline" onClick={() => setResetOpen(false)} data-testid="prefectura-reset-cancel">Cancelar</Button>
            <Button onClick={handleReset} disabled={resetting} data-testid="prefectura-reset-confirm" className="bg-red-600 hover:bg-red-700">
              {resetting ? 'Reiniciando...' : 'Sí, reiniciar'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default PrefecturaDashboard;
